import { useState, useEffect, useMemo } from 'react';
import { useBusiness } from '@/contexts/BusinessContext';
import { useMonth } from '@/contexts/MonthContext';
import { fetchStripeTransactions, type StripeTransaction } from '@/lib/stripeService';
import { decryptStripeKey } from '@/lib/encryption';
import { getMonthTimestamps, getMonthName } from '@/lib/dateUtils';
import { ArrowDownRight, ArrowUpRight, RefreshCw, Search, DollarSign, X, CircleAlert as AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import InfoTooltip from '@/components/InfoTooltip';
import LogoSpinner from '@/components/LogoSpinner';

type FilterType = 'charge' | 'payment' | 'refund' | 'payout' | 'other';

const TYPE_LABELS: Record<string, string> = {
  charge: 'Charge',
  payment: 'Payment',
  refund: 'Refund',
  payment_refund: 'Refund',
  payout: 'Payout',
  transfer: 'Transfer',
  adjustment: 'Adjustment',
  stripe_fee: 'Stripe Fee',
  network_cost: 'Network Cost',
  application_fee: 'App Fee',
};

function getTypeLabel(type: string): string {
  return TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function getTypeBadgeStyle(type: string): string {
  switch (type) {
    case 'charge':
    case 'payment':
      return 'bg-emerald-500/10 text-emerald-400';
    case 'refund':
    case 'payment_refund':
      return 'bg-red-500/10 text-red-700';
    case 'payout':
      return 'bg-blue-500/10 text-blue-700';
    case 'stripe_fee':
    case 'network_cost':
      return 'bg-white/10 text-white';
    default:
      return 'bg-amber-500/10 text-amber-700';
  }
}

function getAmountColor(amount: number): string {
  if (amount > 0) return 'text-emerald-400';
  if (amount < 0) return 'text-red-600';
  return 'text-white';
}

function getAmountIcon(amount: number) {
  if (amount > 0) return <ArrowDownRight size={12} className="text-emerald-400" />;
  if (amount < 0) return <ArrowUpRight size={12} className="text-red-500" />;
  return <Minus size={12} className="text-white" />;
}

function matchesFilter(type: string, filter: FilterType): boolean {
  if (filter === 'charge') return type === 'charge' || type === 'payment';
  if (filter === 'refund') return type === 'refund' || type === 'payment_refund';
  if (filter === 'payout') return type === 'payout';
  if (filter === 'other') return !['charge', 'payment', 'refund', 'payment_refund', 'payout'].includes(type);
  return true;
}

export default function PaymentsPage() {
  const { selectedBusiness } = useBusiness();
  const { selectedMonth } = useMonth();
  const [transactions, setTransactions] = useState<StripeTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('charge');
  const [selectedTxn, setSelectedTxn] = useState<StripeTransaction | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => { loadTransactions(); }, [selectedBusiness, selectedMonth]);

  async function loadTransactions() {
    if (!selectedBusiness) return;
    try {
      setLoading(true);
      setError(null);
      const stripeKey = await decryptStripeKey(selectedBusiness.stripe_key_encrypted);
      if (!stripeKey) {
        setError('No Stripe key configured. Add one in Settings.');
        setTransactions([]);
        return;
      }
      const { startTimestamp, endTimestamp } = getMonthTimestamps(selectedMonth.year, selectedMonth.month);
      const data = await fetchStripeTransactions(stripeKey, startTimestamp, endTimestamp);
      setTransactions(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => transactions.filter(txn => {
    if (!matchesFilter(txn.type, filterType)) return false;
    if (!debouncedSearch.trim()) return true;
    const q = debouncedSearch.toLowerCase();
    return (
      txn.description?.toLowerCase().includes(q) ||
      txn.customer_email?.toLowerCase().includes(q) ||
      txn.customer_name?.toLowerCase().includes(q) ||
      txn.id.toLowerCase().includes(q) ||
      txn.source_id?.toLowerCase().includes(q) ||
      getTypeLabel(txn.type).toLowerCase().includes(q) ||
      (txn.amount / 100).toFixed(2).includes(q)
    );
  }), [transactions, filterType, debouncedSearch]);

  const summary = useMemo(() => ({
    gross: filtered.filter(t => t.type === 'charge' || t.type === 'payment').reduce((s, t) => s + t.amount, 0),
    fees: filtered.reduce((s, t) => s + t.fee, 0),
    refunds: filtered.filter(t => t.type === 'refund' || t.type === 'payment_refund').reduce((s, t) => s + Math.abs(t.amount), 0),
    net: filtered.reduce((s, t) => s + t.net, 0),
  }), [filtered]);

  const filterCounts = useMemo(() => ({
    charge: transactions.filter(t => t.type === 'charge' || t.type === 'payment').length,
    refund: transactions.filter(t => t.type === 'refund' || t.type === 'payment_refund').length,
    payout: transactions.filter(t => t.type === 'payout').length,
    other: transactions.filter(t => !['charge', 'payment', 'refund', 'payment_refund', 'payout'].includes(t.type)).length,
  }), [transactions]);

  const filters: { key: FilterType; label: string; count: number }[] = [
    { key: 'charge', label: 'Charges', count: filterCounts.charge },
    { key: 'refund', label: 'Refunds', count: filterCounts.refund },
    { key: 'payout', label: 'Payouts', count: filterCounts.payout },
    { key: 'other', label: 'Other', count: filterCounts.other },
  ];

  if (loading && transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <LogoSpinner size={48} />
        <p className="text-xs text-white/60">Loading transactions from Stripe...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="glass-header shrink-0 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-white tracking-tight">Transactions</h1>
            <p className="text-xs text-white mt-0.5">
              {getMonthName(selectedMonth.month)} {selectedMonth.year} &middot; {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => loadTransactions()}
            disabled={loading}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-xs font-medium shrink-0 ${
              loading
                ? 'glass-button cursor-not-allowed text-white opacity-50'
                : 'glass-button-active text-white'
            }`}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">{loading ? 'Loading...' : 'Refresh'}</span>
            <span className="sm:hidden">{loading ? '...' : 'Refresh'}</span>
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 px-3 py-2.5 mb-3 bg-red-500/10 border border-red-200 rounded-xl text-xs text-red-700">
            <AlertCircle size={14} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <InfoTooltip title="Gross Revenue" description="Total amount charged to customers before Stripe fees and refunds. This is the raw revenue from the currently filtered transaction set." position="bottom">
            <div className="glass-panel rounded-xl p-3 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-default">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp size={12} className="text-white/70" />
                <span className="text-[11px] font-medium text-white/70">Gross</span>
              </div>
              <span className="text-sm font-bold text-white">${(summary.gross / 100).toFixed(2)}</span>
            </div>
          </InfoTooltip>
          <InfoTooltip title="Processing Fees" description="Total Stripe processing fees deducted from the filtered transactions. Typically 2.9% + 30c per successful charge." position="bottom">
            <div className="glass-panel rounded-xl p-3 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-default">
              <div className="flex items-center gap-1.5 mb-1">
                <Minus size={12} className="text-white" />
                <span className="text-[11px] font-medium text-white">Fees</span>
              </div>
              <span className="text-sm font-bold text-white">${(summary.fees / 100).toFixed(2)}</span>
            </div>
          </InfoTooltip>
          <InfoTooltip title="Refund Total" description="Total amount refunded back to customers in the filtered view. Refunds reduce your net revenue and may also include fee adjustments." position="bottom">
            <div className="glass-panel rounded-xl p-3 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-default">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingDown size={12} className="text-white/70" />
                <span className="text-[11px] font-medium text-white/70">Refunds</span>
              </div>
              <span className="text-sm font-bold text-white">${(summary.refunds / 100).toFixed(2)}</span>
            </div>
          </InfoTooltip>
          <InfoTooltip title="Net Revenue" description="Final amount after all fees and refunds. This is the actual balance impact from the filtered transactions -- the money you keep." position="bottom">
            <div className="glass-panel rounded-xl p-3 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-default">
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign size={12} className="text-white/70" />
                <span className="text-[11px] font-medium text-white/70">Net</span>
              </div>
              <span className="text-sm font-bold text-white">${(summary.net / 100).toFixed(2)}</span>
            </div>
          </InfoTooltip>
        </div>

        <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilterType(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${
                filterType === f.key
                  ? 'glass-button-active text-white'
                  : 'glass-button text-white'
              }`}
            >
              {f.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                filterType === f.key ? 'bg-white/20 text-white' : 'bg-white/15 text-white'
              }`}>
                {f.count}
              </span>
            </button>
          ))}
        </div>

        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-white" size={15} />
          <input
            type="text"
            placeholder="Search by description, email, amount..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-sm focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all duration-200 placeholder:text-white"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-4">
              <DollarSign size={28} className="text-white" />
            </div>
            <h3 className="text-sm font-semibold text-white mb-1">
              {searchQuery || filterType !== 'charge' ? 'No transactions found' : 'No transactions this month'}
            </h3>
            <p className="text-xs text-white">
              {searchQuery ? 'Try adjusting your search' : filterType !== 'charge' ? 'Try a different filter' : 'Transactions will appear once charges are made in Stripe'}
            </p>
          </div>
        ) : (
          <div className="glass-panel rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/20">
                <thead className="bg-white/20">
                  <tr>
                    <th className="px-5 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Description</th>
                    <th className="px-5 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Type</th>
                    <th className="px-5 py-3 text-left text-[11px] font-medium text-white uppercase tracking-wider">Date</th>
                    <th className="px-5 py-3 text-right text-[11px] font-medium text-white uppercase tracking-wider">Gross</th>
                    <th className="px-5 py-3 text-right text-[11px] font-medium text-white uppercase tracking-wider">Fee</th>
                    <th className="px-5 py-3 text-right text-[11px] font-medium text-white uppercase tracking-wider">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {filtered.map(txn => (
                    <tr
                      key={txn.id}
                      className="glass-hover cursor-pointer"
                      onClick={() => setSelectedTxn(txn)}
                    >
                      <td className="px-5 py-3 max-w-[280px]">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                            txn.amount >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'
                          }`}>
                            {getAmountIcon(txn.amount)}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-white truncate">
                              {txn.description || txn.customer_name || txn.customer_email || getTypeLabel(txn.type)}
                            </div>
                            {txn.customer_email && txn.description && (
                              <div className="text-[11px] text-white truncate mt-0.5">{txn.customer_email}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${getTypeBadgeStyle(txn.type)}`}>
                          {getTypeLabel(txn.type)}
                        </span>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <div className="text-xs text-white">
                          {new Date(txn.created * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="text-[11px] text-white mt-0.5">
                          {new Date(txn.created * 1000).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-right">
                        <span className={`text-xs font-medium ${getAmountColor(txn.amount)}`}>
                          {txn.amount < 0 ? '-' : ''}${(Math.abs(txn.amount) / 100).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-right">
                        <span className="text-xs text-white">
                          {txn.fee > 0 ? `-$${(txn.fee / 100).toFixed(2)}` : '$0.00'}
                        </span>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-right">
                        <span className={`text-xs font-semibold ${getAmountColor(txn.net)}`}>
                          {txn.net < 0 ? '-' : ''}${(Math.abs(txn.net) / 100).toFixed(2)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {selectedTxn && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={() => setSelectedTxn(null)}>
          <div className="glass-modal rounded-2xl max-w-md w-full " onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="text-base font-semibold text-white">Transaction Details</h2>
              <button onClick={() => setSelectedTxn(null)} className="text-white p-1 rounded-lg glass-hover">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-0">
              <div className="flex items-center justify-between py-3 border-b border-white/10">
                <span className="text-xs text-white">Type</span>
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${getTypeBadgeStyle(selectedTxn.type)}`}>
                  {getTypeLabel(selectedTxn.type)}
                </span>
              </div>
              {selectedTxn.description && (
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-xs text-white">Description</span>
                  <span className="text-xs font-medium text-white text-right max-w-[60%] truncate">{selectedTxn.description}</span>
                </div>
              )}
              <div className="flex items-center justify-between py-3 border-b border-white/10">
                <span className="text-xs text-white">Gross Amount</span>
                <span className={`text-sm font-semibold ${getAmountColor(selectedTxn.amount)}`}>
                  {selectedTxn.amount < 0 ? '-' : ''}${(Math.abs(selectedTxn.amount) / 100).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-white/10">
                <span className="text-xs text-white">Stripe Fee</span>
                <span className="text-xs text-white">
                  {selectedTxn.fee > 0 ? `-$${(selectedTxn.fee / 100).toFixed(2)}` : '$0.00'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-white/10">
                <span className="text-xs text-white">Net Amount</span>
                <span className={`text-sm font-bold ${getAmountColor(selectedTxn.net)}`}>
                  {selectedTxn.net < 0 ? '-' : ''}${(Math.abs(selectedTxn.net) / 100).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-white/10">
                <span className="text-xs text-white">Date</span>
                <span className="text-xs text-white">
                  {new Date(selectedTxn.created * 1000).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-white/10">
                <span className="text-xs text-white">Currency</span>
                <span className="text-xs font-medium text-white uppercase">{selectedTxn.currency}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-white/10">
                <span className="text-xs text-white">Status</span>
                <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium bg-emerald-500/10 text-emerald-400">
                  {selectedTxn.status}
                </span>
              </div>
              {selectedTxn.customer_name && (
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-xs text-white">Customer</span>
                  <span className="text-xs font-medium text-white">{selectedTxn.customer_name}</span>
                </div>
              )}
              {selectedTxn.customer_email && (
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="text-xs text-white">Email</span>
                  <span className="text-xs text-white">{selectedTxn.customer_email}</span>
                </div>
              )}
              {selectedTxn.source_id && (
                <div className="py-3">
                  <span className="text-xs text-white block mb-2">Source ID</span>
                  <code className="block glass-inner rounded-xl px-3 py-2 text-[11px] text-white break-all font-mono">{selectedTxn.source_id}</code>
                </div>
              )}
              {!selectedTxn.source_id && (
                <div className="py-3">
                  <span className="text-xs text-white block mb-2">Transaction ID</span>
                  <code className="block glass-inner rounded-xl px-3 py-2 text-[11px] text-white break-all font-mono">{selectedTxn.id}</code>
                </div>
              )}
            </div>
            <div className="px-5 pb-5">
              <button onClick={() => setSelectedTxn(null)} className="w-full px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
